import React, { useEffect, useId, useRef } from "react";
import { findFirstComponentOfType } from "../internal/TextExtractor";
import { ColumnWidth, ToggleMeasureBranch, useResizableColumnController } from "../../state/resizable-column-controller.context";

interface ResizableColumnProps {
    id?: string;
    width: ColumnWidth;
    minWidth?: number | string;
    maxWidth?: number | string;
    autoSizeFromToggle?: boolean | string;
    showSeparator?: boolean | string;
    index?: number | string;
    children?: React.ReactNode;
}

function parseBoolean(value: boolean | string | undefined, fallback: boolean): boolean {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        return value.toLowerCase() === "true";
    }
    return fallback;
}

function parseOptionalNumber(value: number | string | undefined): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string") {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return undefined;
}

function measureBranchWidth(element: HTMLElement | null): number {
    if (!element) {
        return 0;
    }
    const rect = element.getBoundingClientRect();
    return Math.ceil(rect.width);
}

function isBranchVisible(element: HTMLElement | null): boolean {
    if (!element || typeof window === "undefined") {
        return false;
    }
    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") {
        return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

function queryToggleBranch(singleElement: HTMLElement | null, selector: string): HTMLElement | null {
    if (!singleElement) {
        return null;
    }
    return singleElement.querySelector(selector) as HTMLElement | null;
}

function queryNestedToggleState(singleElement: HTMLElement | null): boolean | null {
    if (!singleElement) {
        return null;
    }
    const toggleButton = singleElement.querySelector("._toggle-button") as HTMLElement | null;
    if (!toggleButton) {
        return null;
    }
    const expanded = toggleButton.getAttribute("aria-expanded");
    if (expanded === "true") {
        return true;
    }
    if (expanded === "false") {
        return false;
    }
    return null;
}

export function ResizableColumn({
    id,
    width,
    minWidth,
    maxWidth,
    autoSizeFromToggle,
    showSeparator,
    index,
    children,
}: ResizableColumnProps): JSX.Element {
    const generatedId = useId().replace(/:/g, "");
    const columnId = id || `resizable-column-${generatedId}`;
    const controller = useResizableColumnController();
    const registerOrUpdateColumn = controller?.updateColumn;
    const unregisterColumn = controller?.unregisterColumn;
    const reportMeasuredToggleWidth = controller?.reportMeasuredToggleWidth;
    const columnIndex = parseOptionalNumber(index) ?? -1;
    const min = parseOptionalNumber(minWidth);
    const max = parseOptionalNumber(maxWidth);
    const autoSize = parseBoolean(autoSizeFromToggle, false);
    const separatorVisible = parseBoolean(showSeparator, true);

    const controlledRef = useRef<HTMLDivElement | null>(null);
    const fallbackRef = useRef<HTMLDivElement | null>(null);
    const singleRef = useRef<HTMLDivElement | null>(null);
    const lastNestedToggleBranchRef = useRef<ToggleMeasureBranch | null>(null);

    useEffect(() => {
        if (!registerOrUpdateColumn || !unregisterColumn) {
            return;
        }
        registerOrUpdateColumn({
            id: columnId,
            width,
            minWidth: min,
            maxWidth: max,
            autoSizeFromToggle: autoSize,
            showSeparator: separatorVisible,
        });
        return () => unregisterColumn(columnId);
    }, [autoSize, columnId, max, min, registerOrUpdateColumn, separatorVisible, unregisterColumn, width]);

    useEffect(() => {
        if (!autoSize || !reportMeasuredToggleWidth || typeof ResizeObserver === "undefined") {
            return;
        }

        const emit = (): void => {
            const controlledEl = controlledRef.current;
            const fallbackEl = fallbackRef.current;
            const singleEl = singleRef.current;

            if (singleEl && !controlledEl && !fallbackEl) {
                const toggleContentEl = queryToggleBranch(singleEl, "._toggle-content");
                const toggleFallbackEl = queryToggleBranch(singleEl, "._toggle-fallback");
                const nestedToggleOn = queryNestedToggleState(singleEl);
                const toggleContentVisible = isBranchVisible(toggleContentEl);
                const toggleFallbackVisible = isBranchVisible(toggleFallbackEl);

                // Toggle-state reports are emitted only on a real branch CHANGE.  The first
                // observation of a branch state on this column merely latches the state and
                // returns without calling reportMeasuredToggleWidth — the DOM measurement at
                // that point reflects content rendered inside a not-yet-sized column
                // (width:0 / overflow:hidden) and is not a valid column width.  The
                // controller's layout effect owns initial sizing; the toggle then drives
                // resizing on subsequent CHANGES (#2413).
                if (nestedToggleOn === false || (toggleFallbackVisible && !toggleContentVisible)) {
                    if (lastNestedToggleBranchRef.current === "fallback") {
                        return;
                    }
                    const w = measureBranchWidth(toggleFallbackEl);
                    if (w > 0) {
                        const isInitial = lastNestedToggleBranchRef.current === null;
                        lastNestedToggleBranchRef.current = "fallback";
                        if (!isInitial) {
                            reportMeasuredToggleWidth(columnId, w, "fallback");
                        }
                    } else {
                        lastNestedToggleBranchRef.current = null;
                    }
                    return;
                }

                if (nestedToggleOn === true || (toggleContentVisible && !toggleFallbackVisible)) {
                    if (lastNestedToggleBranchRef.current === "controlled") {
                        return;
                    }
                    const w = measureBranchWidth(toggleContentEl);
                    if (w > 0) {
                        const isInitial = lastNestedToggleBranchRef.current === null;
                        lastNestedToggleBranchRef.current = "controlled";
                        if (!isInitial) {
                            reportMeasuredToggleWidth(columnId, w, "controlled");
                        }
                    } else {
                        lastNestedToggleBranchRef.current = null;
                    }
                    return;
                }

                lastNestedToggleBranchRef.current = null;

                const w = measureBranchWidth(singleEl);
                if (w > 0) {
                    reportMeasuredToggleWidth(columnId, w, "single");
                }
                return;
            }

            if (controlledEl && fallbackEl) {
                const controlledVisible = isBranchVisible(controlledEl);
                const fallbackVisible = isBranchVisible(fallbackEl);
                if (controlledVisible && !fallbackVisible) {
                    reportMeasuredToggleWidth(columnId, measureBranchWidth(controlledEl), "controlled");
                } else if (fallbackVisible && !controlledVisible) {
                    reportMeasuredToggleWidth(columnId, measureBranchWidth(fallbackEl), "fallback");
                } else if (controlledVisible && fallbackVisible) {
                    reportMeasuredToggleWidth(columnId, measureBranchWidth(controlledEl), "controlled");
                }
                return;
            }

            if (controlledEl && !fallbackEl) {
                if (isBranchVisible(controlledEl)) {
                    const w = measureBranchWidth(controlledEl);
                    if (w > 0) {
                        reportMeasuredToggleWidth(columnId, w, "controlled");
                    }
                }
                return;
            }

            if (fallbackEl && !controlledEl) {
                if (isBranchVisible(fallbackEl)) {
                    const w = measureBranchWidth(fallbackEl);
                    if (w > 0) {
                        reportMeasuredToggleWidth(columnId, w, "fallback");
                    }
                }
            }
        };

        const observer = new ResizeObserver(() => emit());
        let mutationObserver: MutationObserver | null = null;
        if (controlledRef.current) {
            observer.observe(controlledRef.current);
        }
        if (fallbackRef.current) {
            observer.observe(fallbackRef.current);
        }
        if (singleRef.current) {
            observer.observe(singleRef.current);
            const toggleContentEl = queryToggleBranch(singleRef.current, "._toggle-content");
            const toggleFallbackEl = queryToggleBranch(singleRef.current, "._toggle-fallback");
            if (toggleContentEl) {
                observer.observe(toggleContentEl);
            }
            if (toggleFallbackEl) {
                observer.observe(toggleFallbackEl);
            }

            if (typeof MutationObserver !== "undefined") {
                mutationObserver = new MutationObserver(() => emit());
                mutationObserver.observe(singleRef.current, {
                    subtree: true,
                    childList: true,
                    attributes: true,
                    attributeFilter: ["class", "style", "hidden", "aria-expanded"],
                });
            }
        }

        emit();

        return () => {
            observer.disconnect();
            mutationObserver?.disconnect();
        };
    }, [autoSize, columnId, reportMeasuredToggleWidth, children]);

    const widthPx = controller?.getColumnWidth(columnId) || 0;
    const controlledChild = findFirstComponentOfType(children, "controlled");
    const fallbackChild = findFirstComponentOfType(children, "fallback");
    const hasSlots = !!(controlledChild || fallbackChild);

    return (
        <>
            <div
                id={columnId}
                style={{
                    flex: "0 0 auto",
                    width: `${Math.max(0, widthPx)}px`,
                    minWidth: 0,
                    overflow: "hidden",
                }}
            >
                {controlledChild && (
                    <div ref={controlledRef} className="_rc-branch _rc-controlled">
                        {controlledChild.props.children}
                    </div>
                )}
                {fallbackChild && (
                    <div ref={fallbackRef} className="_rc-branch _rc-fallback">
                        {fallbackChild.props.children}
                    </div>
                )}
                {!hasSlots && (
                    <div ref={singleRef} className="_rc-branch _rc-single">
                        {children}
                    </div>
                )}
            </div>
            {controller && separatorVisible && columnIndex >= 0 && controller.getColumnIdByIndex(columnIndex + 1) && (
                <div
                    {...controller.getSeparatorProps(columnIndex)}
                    style={{
                        flex: "0 0 auto",
                        width: "8px",
                        cursor: "col-resize",
                        userSelect: "none",
                        touchAction: "none",
                    }}
                />
            )}
        </>
    );
}
